import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

interface FlyToCartContextValue {
  /** Cart icon target — header registers itself here. */
  setCartTargetRef: (ref: RefObject<HTMLElement | null>) => void;
  /** Trigger the fly animation from a source element to the cart icon. */
  flyToCart: (sourceEl: HTMLElement, imageUrl?: string | null) => void;
  /** Bump key — increments each time something is added; useful for triggering badge bump animations elsewhere. */
  bumpKey: number;
}

const FlyToCartContext = createContext<FlyToCartContextValue | null>(null);

export function useFlyToCart() {
  const ctx = useContext(FlyToCartContext);
  if (!ctx) throw new Error('useFlyToCart must be used inside <FlyToCartProvider>');
  return ctx;
}

export function FlyToCartProvider({ children }: { children: ReactNode }) {
  const targetRef = useRef<RefObject<HTMLElement | null> | null>(null);
  const [bumpKey, setBumpKey] = useState(0);

  const setCartTargetRef = useCallback((ref: RefObject<HTMLElement | null>) => {
    targetRef.current = ref;
  }, []);

  const flyToCart = useCallback((sourceEl: HTMLElement, imageUrl?: string | null) => {
    const targetEl = targetRef.current?.current;
    if (!targetEl) {
      // No cart visible (e.g. mobile collapsed) — still fire bump for downstream animations.
      setBumpKey((k) => k + 1);
      return;
    }

    const sourceRect = sourceEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();

    const clone = document.createElement('div');
    clone.className = 'pro-fly-clone';
    clone.style.left = `${sourceRect.left}px`;
    clone.style.top = `${sourceRect.top}px`;
    clone.style.width = `${sourceRect.width}px`;
    clone.style.height = `${sourceRect.height}px`;

    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.borderRadius = '12px';
      clone.appendChild(img);
    } else {
      clone.style.background = 'var(--pro-primary)';
      clone.style.borderRadius = '12px';
    }

    document.body.appendChild(clone);

    // Force reflow before animating
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    clone.offsetWidth;

    // Animate to cart icon
    requestAnimationFrame(() => {
      clone.style.left = `${targetRect.left + targetRect.width / 2 - 14}px`;
      clone.style.top = `${targetRect.top + targetRect.height / 2 - 14}px`;
      clone.style.width = '28px';
      clone.style.height = '28px';
      clone.style.opacity = '0.2';
      clone.style.transform = 'rotate(15deg)';
    });

    const onEnd = () => {
      clone.removeEventListener('transitionend', onEnd);
      clone.remove();
      setBumpKey((k) => k + 1);
    };
    clone.addEventListener('transitionend', onEnd);
    // Safety net in case transitionend is missed
    setTimeout(() => {
      if (clone.isConnected) {
        clone.remove();
        setBumpKey((k) => k + 1);
      }
    }, 600);
  }, []);

  const value = useMemo<FlyToCartContextValue>(
    () => ({ setCartTargetRef, flyToCart, bumpKey }),
    [setCartTargetRef, flyToCart, bumpKey],
  );

  return <FlyToCartContext.Provider value={value}>{children}</FlyToCartContext.Provider>;
}
